/**
 * Feature-flag gating for Help Center content.
 *
 * An article or collection may carry an optional `featureFlag` string:
 *
 *   "serializedEquipment"   → show ONLY when that flag is on
 *   "!serializedEquipment"  → show ONLY when that flag is off (legacy content)
 *   undefined               → always show (the default for every existing article)
 *
 * IMPORTANT — the Help Center is a PUBLIC route (`/help/rocketplan`, no auth).
 * A signed-out visitor has no company, so every flag reads false and only
 * un-gated plus negated ("!flag") content is visible. That is deliberate: a
 * prospect must never be shown docs for a feature their company has not been
 * cut over to. Positively-gated articles are therefore visible only to a
 * signed-in user whose company has the flag on AND whose flags are already in
 * the store (there is no redux persistence, so a cold load of the help URL has
 * no flags — see HelpCenter.tsx).
 */

export type FeatureFlags = Record<string, boolean | string | undefined>;

export interface FeatureGated {
  featureFlag?: string;
}

/**
 * Gates applied to articles that live in the GENERATED Intercom export
 * (`data/rocketplan-articles.json`, rebuilt by `scripts/build_help_center.py`).
 *
 * They are keyed by article id and applied at render time precisely because that
 * file is a build artifact — editing it directly would be silently reverted the
 * next time anyone re-syncs from Intercom. Hand-authored articles carry their own
 * `featureFlag` inline in `data/local-articles.json` instead.
 */
export const ARTICLE_FLAG_OVERRIDES: Record<string, string> = {
  // The count-based inventory walkthrough describes a screen that serialized
  // companies no longer have, so hide it once they are cut over.
  10349691: '!serializedEquipment',
};

/**
 * Apply {@link ARTICLE_FLAG_OVERRIDES} to a tree of collections.
 *
 * An inline `featureFlag` always wins, so a hand-authored article is never
 * overridden by an id collision with the export.
 */
export function applyFlagOverrides<T extends { articles: any[]; children: T[] }>(collections: T[]): T[] {
  return collections.map((collection) => ({
    ...collection,
    articles: collection.articles.map((article) => {
      const override = ARTICLE_FLAG_OVERRIDES[article.id];

      return override && !article.featureFlag ? { ...article, featureFlag: override } : article;
    }),
    children: applyFlagOverrides(collection.children),
  }));
}

/**
 * Whether a single gated item should render for the given flag values.
 */
export function isVisibleForFlags(item: FeatureGated, flags: FeatureFlags): boolean {
  const gate = item.featureFlag;

  if (!gate) {
    return true;
  }

  const negated = gate.startsWith('!');
  const key = negated ? gate.slice(1) : gate;
  const enabled = flags[key] === true;

  return negated ? !enabled : enabled;
}

interface GatedCollection extends FeatureGated {
  id?: string;
  name?: string;
  articles: FeatureGated[];
  children: GatedCollection[];
}

/**
 * Where a hand-authored collection is grafted into the generated tree.
 *
 * Keyed by the local collection id; the value is the name of the generated
 * top-level collection it should nest under. A local collection whose parent is
 * not found is appended at the top level rather than dropped.
 */
const LOCAL_COLLECTION_PARENTS: Record<string, string> = {
  90000000: 'RocketPlan Tutorials',
};

/**
 * Merge hand-authored collections (`data/local-articles.json`) into the generated
 * Intercom tree, without touching the generated file on disk.
 */
export function mergeLocalCollections<T extends GatedCollection>(generated: T[], local: T[]): T[] {
  if (local.length === 0) {
    return generated;
  }

  const merged = generated.map((collection) => ({ ...collection, children: [...collection.children] }));

  local.forEach((collection) => {
    const parentName = LOCAL_COLLECTION_PARENTS[String(collection.id)];
    const parent = parentName ? merged.find((c) => c.name === parentName) : undefined;

    if (parent) {
      parent.children.push(collection);
    } else {
      merged.push({ ...collection });
    }
  });

  return merged;
}

/**
 * Recursively filter collections and their articles by flag state.
 *
 * A collection is dropped when its own gate fails, and also when the filtering
 * leaves it with no articles and no surviving children — so gating the only
 * article in a section never leaves an empty heading behind.
 */
export function filterCollections<T extends GatedCollection>(collections: T[], flags: FeatureFlags): T[] {
  return collections.reduce<T[]>((kept, collection) => {
    if (!isVisibleForFlags(collection, flags)) {
      return kept;
    }

    const articles = collection.articles.filter((article) => isVisibleForFlags(article, flags));
    const children = filterCollections(collection.children, flags);

    if (articles.length === 0 && children.length === 0) {
      return kept;
    }

    kept.push({ ...collection, articles, children });

    return kept;
  }, []);
}
